import {
  COURIER_CAPABILITIES,
  type CourierCapability,
  type LogisticsPartner,
} from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Loader2, RefreshCw, Unplug } from "lucide-react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ExactTime } from "@/modules/orders/screens/TrackingParts";
import { waitWords, type OutcomeMessage } from "@/modules/orders/tracking";
import {
  type CheckNow,
  useCheckNow,
} from "@/modules/settings/logistics/useCheckNow";

const CAPABILITY_LABEL: Record<CourierCapability, string> = {
  tracking: "Tracking",
  rates: "Rates",
  create_shipment: "Creating shipments",
  failed_delivery_actions: "Failed-delivery actions",
  remittance: "Remittance",
};

/** What a partner is used for, said as it stands today. */
function usage(
  capability: CourierCapability,
  partner: LogisticsPartner,
): { text: string; active: boolean } {
  if (!partner.capabilities[capability]) {
    return { text: "not used yet", active: false };
  }
  return partner.connected
    ? { text: "in use", active: true }
    : { text: "ready, waiting for its connection", active: false };
}

/**
 * Why a partner is not connected, in plain words: keys are deployment secrets,
 * so there is nothing to enter on this screen and nothing to show.
 */
function notConnectedWhy(partner: LogisticsPartner): string {
  if (partner.kind === "fallback") {
    return "Shopify's credentials are set by the deployment, not here, so there is no way to read scan history from this screen. Until they are, shipments no partner covers have no tracking.";
  }
  return `The keys for ${partner.name} are set by the deployment, not on this screen, so there is nothing to enter here. Until they are, nothing is checked through it and its shipments are followed through Shopify's scan history instead.`;
}

/** A check's outcome, drawn under the card or the header it came from. */
export function OutcomeLine({
  message,
  className,
}: {
  message: OutcomeMessage | null;
  className?: string;
}) {
  return (
    <div aria-live="polite" role="status" className={className}>
      {message ? (
        <p
          className={cn(
            "text-sm",
            message.tone === "problem" && "text-destructive",
            message.tone === "ok" && "text-emerald-700 dark:text-emerald-300",
            message.tone === "info" && "text-muted-foreground",
          )}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

function CheckButton({
  scope,
  source,
  check,
  label,
  variant = "outline",
}: {
  scope: string;
  source?: string;
  check: CheckNow;
  label: string;
  variant?: "outline" | "default";
}) {
  const pending = check.pendingScope !== null;
  const mine = check.pendingScope === scope;
  const wait = check.waitSeconds(scope);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button
        type="button"
        variant={variant}
        size="sm"
        className="pointer-coarse:h-11 pointer-coarse:px-4"
        disabled={pending || wait > 0}
        aria-busy={mine}
        onClick={() => {
          check.check(source);
        }}
      >
        {mine ? (
          <Loader2 className="animate-spin" aria-hidden />
        ) : (
          <RefreshCw aria-hidden />
        )}
        {mine ? "Starting…" : label}
      </Button>
      {wait > 0 && !mine ? (
        <span className="text-muted-foreground text-xs">
          Available again in {waitWords(wait)}
        </span>
      ) : null}
    </div>
  );
}

function PartnerCard({
  partner,
  check,
  canCheck,
}: {
  partner: LogisticsPartner;
  check: CheckNow;
  canCheck: boolean;
}) {
  const mine =
    check.result?.scope === partner.key ? check.result.message : null;
  const checkable =
    canCheck && partner.connected && partner.capabilities.tracking;

  return (
    <Card className="gap-3 py-4" aria-label={partner.name}>
      <CardHeader className="gap-1 px-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-base">{partner.name}</CardTitle>
          {partner.connected ? (
            <Badge
              variant="outline"
              className="gap-1.5 border-transparent bg-tint-green font-medium text-tint-green-foreground"
            >
              <CircleCheck aria-hidden />
              Connected
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-muted-foreground bg-muted gap-1.5 font-medium"
            >
              <Unplug aria-hidden />
              Not connected
            </Badge>
          )}
        </div>
        {partner.kind === "fallback" ? (
          <p className="text-muted-foreground text-sm">
            Used for every shipment no connected partner covers. Read only:
            nothing is written to Shopify.
          </p>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3 px-4 text-sm">
        {partner.connected ? null : (
          <p className="text-muted-foreground">{notConnectedWhy(partner)}</p>
        )}

        <div>
          <h3 className="text-muted-foreground mb-1 text-xs font-medium">
            Used for
          </h3>
          <ul className="space-y-0.5">
            {COURIER_CAPABILITIES.map((capability) => {
              const { text, active } = usage(capability, partner);
              return (
                <li
                  key={capability}
                  className="flex flex-wrap items-baseline justify-between gap-x-3"
                >
                  <span className={cn(!active && "text-muted-foreground")}>
                    {CAPABILITY_LABEL[capability]}
                  </span>
                  <span
                    className={cn(
                      "text-xs",
                      active
                        ? "font-medium text-emerald-700 dark:text-emerald-300"
                        : "text-muted-foreground",
                    )}
                  >
                    {text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground text-xs">
              Open shipments followed
            </dt>
            <dd className="tabular-nums">{partner.openShipments}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Last checked</dt>
            <dd>
              {partner.lastSuccessAt ? (
                <ExactTime value={partner.lastSuccessAt} />
              ) : (
                <span className="text-muted-foreground">Never</span>
              )}
            </dd>
          </div>
        </dl>

        {partner.lastError ? (
          <div className="border-destructive/40 bg-destructive/10 rounded-md border p-2.5">
            <p className="text-destructive text-xs font-medium">
              Last problem
              {partner.lastErrorAt ? (
                <>
                  {" "}
                  <ExactTime
                    value={partner.lastErrorAt}
                    className="text-destructive"
                  />
                </>
              ) : null}
            </p>
            <p className="text-sm wrap-anywhere">{partner.lastError}</p>
          </div>
        ) : null}

        {checkable ? (
          <div className="space-y-2">
            <CheckButton
              scope={partner.key}
              source={partner.key}
              check={check}
              label="Check now"
            />
            <OutcomeLine message={mine} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Settings → Logistics partners, the partner cards (#527, Part B): every partner
 * the system knows and Shopify's scan history, each with whether it is
 * connected, what it is used for, how many open shipments it is following, when
 * it was last checked, and its last problem.
 *
 * No key is shown and none can be entered: they are deployment secrets, and a
 * partner that is not connected says so and why. "Check now" is offered to
 * whoever holds `settings.sync.trigger` and only on a partner that can be
 * checked; the server refuses anyone else however this looks.
 */
export function PartnerCards() {
  const data = useDataSource();
  const permissions = usePermissions();
  const canCheck = can(permissions, "settings.sync.trigger");
  const check = useCheckNow();

  const query = useQuery({
    queryKey: ["tracking", "partners"],
    queryFn: () => data.tracking.listPartners(),
  });

  const everything =
    check.result?.scope === "all" ? check.result.message : null;

  return (
    <section aria-labelledby="partners-heading" className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <h2 id="partners-heading" className="text-base font-semibold">
            Partners
          </h2>
          <p className="text-muted-foreground text-sm">
            Who follows each shipment to the customer, or back. Every shipment
            with a tracking number is checked through the partner that booked it
            when that partner is connected, and through Shopify&apos;s scan
            history when none is.
          </p>
        </div>
        {canCheck && query.data ? (
          <CheckButton
            scope="all"
            check={check}
            label="Check all now"
            variant="default"
          />
        ) : null}
      </div>
      <OutcomeLine message={everything} />

      {query.isPending ? (
        <TableSkeleton rows={3} />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {query.data.map((partner) => (
            <PartnerCard
              key={partner.key}
              partner={partner}
              check={check}
              canCheck={canCheck}
            />
          ))}
        </div>
      )}
    </section>
  );
}
